import { Attrs, EventListeners, MinzeElement, Reactive } from "minze";

import { MaskOptions, applyMask, parseInlineStyle } from "../../../modules/utils";

export interface TjInput {
  textarea: boolean;
}

export class TjInput extends MinzeElement {
  reactive: Reactive = [
    ["value", "", true],
    ["tooltipOpened", false]
  ];

  attrs: Attrs = [
    ["disabled", false],
    ["placeholder", ""],
    ["height", "auto"],
    "type",
    "mask",
    ["error", false],
    "name",
    "id",
    ["default-value", ""],
    ["date-format", "YYYY-MM-DD"],
    "label",
    ["padding", "7px 14px"],
    ["fontSize", "16px"],
    ["marginTop", "0"],
    ["clear-button-enabled", false],
    "placeholder-color",
    ["border", "1px solid #AAA"],
    ["inner-style", ""],
    ["readonly", false],
    ["tooltip", false],
    ["textarea", false],
    ["staticPlaceholder", false]
  ];

  static observedAttributes = [
    "inner-style",
    "placeholder",
    "height",
    "type",
    "name",
    "id",
    "label",
    "default-value",
    "padding",
    "error",
    "readonly",
    "tooltip"
  ];

  fieldType: string = "text";

  onReady() {
    if (this.defaultValue && typeof this.defaultValue === "string") {
      this.value = this.defaultValue;
    }

    this.fieldType = this.type as string;
  }

  onRender() {
    if (this.clearButtonEnabled) {
      if (!this.shadowRoot) return;
      const input = this.shadowRoot.querySelector("input");
      if (!input) return;
      input.selectionStart = input.selectionEnd = input.value.length;
      input?.focus();
    }
  }

  onAttributeChange = (name: string, oldValue: string, newValue: string) => {
    if (name === "type") {
      this.fieldType = newValue;
    }

    if ((name === "default-value" || name === "value") && oldValue !== newValue) {
      let valueToSet = newValue;
      let oldSetValue = oldValue;

      if (newValue === null) {
        valueToSet = "";
      }

      if (oldValue === null) {
        oldSetValue = "";
      }

      if (oldValue === null) {
        oldValue = "";
      }

      if (oldSetValue !== valueToSet) {
        this.value = valueToSet;
        //only value can update input directly if changed from outside
        //or else you risk forever loop
        if (name === "value") {
          const currentElement = this.select(`[data-main-input="true"]`);
          if (currentElement as any) {
            (currentElement as any).value = valueToSet;
          }
        }
      }

    }


    if ((name === "default-value" || name === "value") && oldValue !== newValue) {

    }

    this.paddingValue = this.padding ? this.padding : "7px 14px";
    this.fontSizeValue = this.fontSize ? this.fontSize : "16px";
    this.heightValue = this.height ? this.height : "auto";
  };

  closeTooltip = () => this.tooltipOpened = false;
  openTooltip = () => this.tooltipOpened = true;

  html = () => {
    let fieldType = this.fieldType;

    if (fieldType === "password" && this.showPassword) {
      fieldType = "text";
    }

    let showError = typeof this.error === "string" && this.error.length > 0;

    return `
      <tj-box height="auto">
        ${this.label ? `
          <tj-box display="flex" align-items="center">
            <label for="${this.id}">${this.label}</label>
            ${this.tooltipOpened ? `<div class="close-overlay"></div>`: ''}
            ${this.tooltip ? `
              <tj-box ml="4px" position="relative">
                <tj-help-icon></tj-help-icon>
                <dialog class="tooltip" ${this.tooltipOpened ? 'open' : ''}>
                  <slot name="tooltip-content"></slot>
                </dialog>
              </tj-box>
            ` : ``}
          </tj-box>
        ` : ""}
        <div class="input-container">
        ${this.textarea ? `
          <textarea
            ${this.disabled ? `disabled` : ""}
            ${this.staticPlaceholder ? `placeholder="${this.placeholder || ""}"` : ""}
            ${fieldType ? `type="${fieldType}"` : ""}
            data-main-input="true"
            name="${this.name}"
            id="${this.id}"
            ${this.value ? `value="${this.value}"` : ""}
            mask="${this.mask}"
            class="input ${this.error ? "error" : ""} ${this.value ? "has-value" : ""}"
            rows="8"
            ${this.readonly ? `readonly` : ""}
          >${this.value}</textarea>
        ` : `
          <input
            ${this.disabled ? `disabled` : ""}
            ${this.staticPlaceholder ? `placeholder="${this.placeholder || ""}"` : ""}
            ${fieldType ? `type="${fieldType}"` : ""}
            data-main-input="true"
            name="${this.name}"
            id="${this.id}"
            ${this.value ? `value="${this.value}"` : ""}
            mask="${this.mask}"
            class="input ${this.error ? "error" : ""} ${this.value ? "has-value" : ""}"
            ${this.readonly ? `readonly` : ""}
          />
        `}
        ${!this.staticPlaceholder ? `<label class="placeholder-label">${this.placeholder || ""}</label>` : ""}
        ${this.clearButtonEnabled ? `
          ${this.value ? `
            <button name="clearInput" class="clear-input-button">
              <tj-clear-input-icon></tj-clear-input-icon>
            </button>
          ` : ''}
        ` : ``}
        ${this.type == "password" ? `
          <button name="showPassword" class="show-password-button">
            ${this.showPassword ? `<tj-show-password-icon></tj-show-password-icon>` : `<tj-hide-password-icon></tj-hide-password-icon>`}
          </button>
        ` : ``}
        </div>
      </tj-box>
      ${showError ? `<tj-error error="${this.error}"></tj-error>` : ""}
      `;
  }

  css = () => {
    const borderValue = this.border ? this.border : "1px solid #AAA"
    return `
    :host {
      width: 100%;
      position: relative;
      box-sizing: border-box;
      display: block;
    }
   
    .input:focus-visible {
      outline: unset;
    }
    .input-container {
      position: relative;
      ${this.marginTop ? `margin-top: ${this.marginTop};` : "0;"}
    }

    .input {
      width: 100%;
      border: ${borderValue ? borderValue : "unset"};
      ${this.heightValue ? `height: ${this.heightValue};` : ""}
      ${this.fontSizeValue ? `font-size: ${this.fontSizeValue};` : ""}
      ${this.paddingValue ? `padding: ${this.paddingValue};` : ""}
      line-height: 150%;
      background: #fff;
      color: #133762;
      border-radius: 8px;
      font-family: inherit;
      resize: none;
    }
    
    .input:focus {
      border-color: #068FC3;
    }
    
    .input:focus ~ label.placeholder-label {
      color: #068FC3;
    }
    
    .input:focus ~ label.placeholder-label, .input.has-value ~ label.placeholder-label {
      top: -8px;
      left: 11px;
      font-size: 11px;
      background: white;
      padding: 0 4px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
    }
    
    ${this.placeholderColor ? `
      ::placeholder {
        color: ${this.placeholderColor};
        opacity: 1;
      }
      
      :-ms-input-placeholder {
        color: ${this.placeholderColor};
      }
      
      ::-ms-input-placeholder {
        color: ${this.placeholderColor};
      }
    ` : ``}

    label {
      color:  #1E007D;
      font-size: 14px;
      line-height: 17px;
      font-weight: 700;
      margin-bottom: 4px;
    }
    
    label.placeholder-label {
      margin: 0;
      color: #133762;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
      position: absolute;
      pointer-events: none;
      left: 14px;
      top: 8px;
      max-width: calc(100% - 14px * 2);
      white-space: nowrap;
      overflow: hidden;
      transition: 0.2s ease all;
    }

    .show-password-button {
      cursor: pointer;
      position: absolute;
      right: 5px;
      top: 50%;
      transform: translateY(-50%);
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
    }
    
    .show-password-button tj-close-icon {
    }
    
    .clear-input-button {
      cursor: pointer;
      position: absolute;
      right: 5px;
      width: 28px;
      height: 28px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      justify-content: center;
      align-items: center;
      background: transparent;
      border: none;
    }
    
    .clear-input-button tj-close-icon {
    }
   
    .error {
      border-color: #DF374B;
    }
    
    tj-show-password-icon, tj-hide-password-icon {
      width: 20px;
      height: 20px;
    }
    
    tj-help-icon {
      cursor: pointer;
    }
    
    .tooltip {
      z-index: 1;
      border: 1px solid rgba(30, 0, 125, 0.3);
      box-shadow: 0px 4px 16px rgba(15, 0, 62, 0.15);
      border-radius: 8px;
      padding: 16px;
      font-weight: 500;
      font-size: 14px;
      line-height: 150%;
      color: #1E007D;
      max-width: 90vw;
      width: 340px;
    }
    
    .close-overlay {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1;
    }
 
    ${parseInlineStyle(this.innerStyle as string)}
  `};

  handleInput = (event: Event) => {
    const currentValue = this.value;
    const target = event.target as HTMLInputElement;
    let value = target.value;
    let pressedKey = (event as KeyboardEvent)?.key as string;
    if (this.mask && pressedKey !== "Backspace") {
      value = applyMask(value, {
        mask: this.mask as string,
        patternsMap: {
          'A':/[a-zA-Z]/,
          '#':/\d/,
          '*': /./
        } as MaskOptions['patternsMap'],
      });
      target.value = value;
    }
    this.value = value;
    if (!currentValue || !value) this.rerender();
  };

  clearInput = () => {
    if (this.value === "") return;

    this.value = "";
    this.rerender(true);
  }

  onPasswordVisibilityChange = () => {
    this.showPassword = !this.showPassword;
    this.rerender();
  }

  eventListeners: EventListeners = [
    ["input", "keyup", this.handleInput.bind(this)],
    ["input", "change", this.handleInput.bind(this)],
    ["textarea", "keyup", this.handleInput.bind(this)],
    ["textarea", "change", this.handleInput.bind(this)],
    [".clear-input-button", "click", this.clearInput.bind(this)],
    ["[name=\"showPassword\"]", "click", this.onPasswordVisibilityChange.bind(this)],
    ["tj-help-icon", "click", this.openTooltip],
    [".close-overlay", "click", this.closeTooltip]
  ];
}
